import clsx from "clsx"
import { Select as SelectPrimitive } from "radix-ui"
import type { ReactNode } from "react"

import { type SX, theme } from "../../theme"
import { Button, type ButtonProps } from "../Button"
import { useButtonGroup } from "../ButtonGroup"
import { Icon, type IconName } from "../Icon"
import { Text } from "../Text"
import styles from "./Select.module.css"

export type SelectProps<VALUE extends string = string> = {
  disabled?: boolean
  id?: string
  name?: string
  size?: "small" | "medium" | "large"
  color?: "grey" | "purple"
  children?: ReactNode
  placeholder?: ReactNode
  autoComplete?: string
  value?: VALUE
  side?: "bottom" | "top" | "left" | "right"
  sideOffset?: number
  flexContent?: boolean
  /**
   * Prominent selects should have bold text for better visibility.
   */
  boldTriggerText?: boolean
  sx?: SX

  startIcon?: IconName
  endIcon?: IconName
  onlyStartIcon?: boolean

  renderTrigger?: (params: RenderTriggerParams) => ReactNode
  renderStartIcon?: (icon: ReactNode) => ReactNode

  onValueChange?: (value: VALUE) => void
} & (
  | {
      constrainContentWidth: true
      position?: "popper"
    }
  | {
      constrainContentWidth?: false
      position?: "popper" | "item-aligned"
    }
)

/**
 * A Select component supporting string text options.
 * Empty string options are not supported.
 */
export function Select<VALUE extends string>(props: SelectProps<VALUE>) {
  const buttonGroup = useButtonGroup()

  const {
    children,
    disabled = buttonGroup?.disabled ?? false,
    id,
    placeholder,
    color = buttonGroup?.color ?? "purple",
    size = buttonGroup?.size ?? "medium",
    renderTrigger = ({ button }) => button,
    startIcon,
    renderStartIcon,
    endIcon = "arrowDropDown",
    onlyStartIcon = false,
    constrainContentWidth,
    side,
    sideOffset,
    flexContent,
    boldTriggerText = false,
    sx,
    name,
    autoComplete,
    value,
    onValueChange,
  } = props

  // constrainContentWidth relies on --radix-select-trigger-width
  // which is only available when using position="popper"
  const position =
    props.position ?? (constrainContentWidth ? "popper" : "item-aligned")

  const textColor = !value && color === "grey" ? "placeholder" : undefined

  const buttonProps: ButtonProps = {
    sx,
    startIcon,
    renderStartIcon,
    endIcon: onlyStartIcon ? undefined : endIcon,
    renderEndIcon,
    size,
    color,
    textColor,
    textWeight: boldTriggerText ? "bold" : "normal",
    disabled,
    flexContent,
  }

  const button = (
    <SelectPrimitive.Trigger asChild disabled={disabled} id={id}>
      {onlyStartIcon ? (
        <SelectPrimitive.Value asChild>
          <Button {...buttonProps} />
        </SelectPrimitive.Value>
      ) : (
        <Button {...buttonProps}>
          <SelectPrimitive.Value placeholder={placeholder} />
        </Button>
      )}
    </SelectPrimitive.Trigger>
  )

  return (
    <SelectPrimitive.Root
      name={name}
      autoComplete={autoComplete}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
    >
      {renderTrigger({
        button,
        disabled,
      })}
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          collisionPadding={parseFloat(theme.space[16])}
          position={position}
          side={side}
          sideOffset={sideOffset}
          className={clsx(
            styles.content,
            constrainContentWidth && styles.constrainContentWidth,
          )}
        >
          <SelectPrimitive.Viewport>{children}</SelectPrimitive.Viewport>
          <SelectPrimitive.ScrollUpButton
            className={clsx(styles.scrollButton, styles.scrollUpButton)}
          >
            <Icon
              className={styles.scrollButtonIcon}
              name="keyboardArrowUp"
              size="large"
            />
          </SelectPrimitive.ScrollUpButton>
          <SelectPrimitive.ScrollDownButton
            className={clsx(styles.scrollButton, styles.scrollDownButton)}
          >
            <Icon
              className={styles.scrollButtonIcon}
              name="keyboardArrowDown"
              size="large"
            />
          </SelectPrimitive.ScrollDownButton>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  )
}

interface RenderTriggerParams {
  button: ReactNode
  disabled: boolean
}

interface SelectItemProps {
  icon?: IconName
  children?: ReactNode
  disabled?: boolean
  textValue?: string
  renderStartIcon?: (icon: ReactNode) => ReactNode
  size?: "small" | "medium" | "large"
  value: string
}

export function SelectItem(props: SelectItemProps) {
  const {
    children,
    icon,
    renderStartIcon = (icon) => icon,
    size = "medium",
    value,
    ...otherProps
  } = props

  return (
    <SelectPrimitive.Item
      {...otherProps}
      value={value}
      className={clsx(styles.item, styles[`itemPadding-${size}`])}
    >
      {renderStartIcon(icon && <Icon name={icon} size="small" />)}
      <Text component="span" noWrap sx={{ flexGrow: 1, order: 1 }}>
        <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      </Text>
      <SelectPrimitive.ItemIndicator asChild className={styles.itemIndicator}>
        <Icon name="check" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  )
}

export function SelectSeparator() {
  return <SelectPrimitive.Separator className={styles.separator} />
}

interface SelectFooterActionProps {
  onClick: () => void
  iconName: IconName
  label: string
}

export function SelectFooterAction(props: SelectFooterActionProps) {
  const { onClick, iconName, label } = props
  return (
    <div
      className={styles.footerAction}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      role="button"
      tabIndex={0}
    >
      <Icon name={iconName} size="large" color="grey11" />
      <Text>{label}</Text>
    </div>
  )
}

function renderEndIcon(icon: ReactNode) {
  return <SelectPrimitive.Icon asChild>{icon}</SelectPrimitive.Icon>
}
